import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { ApiError } from './api'
import type { YearFilter } from '@/types/domain'

/* TanStack Query holds all server state; query keys mirror API resources
 * one to one. NFR-2 liveness is satisfied
 * by current-on-load/refresh through invalidation — there is no realtime
 * transport in v1, so nothing here subscribes or polls by default. */

/* The forced password change (QW-5).
 *
 * While an account is flagged, every authenticated route but `GET /session`
 * and `PUT /session/password` answers 403 `password-change-required`. The
 * redirect belongs to `RequireSession`, which reads the SESSION — and the
 * cached session can be older than the flag: an admin resets the password of
 * a colleague who is signed in, and their next request is refused while their
 * cache still says "not flagged". So wherever the refusal surfaces, query or
 * mutation, the session is read again and the guard does the rest. The session
 * read itself is exempt server-side, so this cannot loop. */
function onRefusal(error: unknown) {
  if (
    error instanceof ApiError &&
    error.status === 403 &&
    error.code === 'password-change-required'
  ) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.session })
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onRefusal }),
  mutationCache: new MutationCache({ onError: onRefusal }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry a decision the server has already made: the answer will
        // not change, and retrying a 403 just delays the message.
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
    },
  },
})

/** Query key factory. One entry per API resource; every key starts with its
 *  resource name so a feature can invalidate its whole subtree at once. */
export const queryKeys = {
  session: ['session'] as const,

  dashboard: {
    all: ['dashboard'] as const,
    /* The range argument stays in the signature and out of the key until a
       figure actually varies by it (Stories 6.3, 6.5). Keying on a range the
       server ignores would cache four identical responses under four keys, so
       switching the picker would refetch and re-render the same numbers. */
    summary: (range?: string) =>
      (range === undefined
        ? (['dashboard', 'summary'] as const)
        : (['dashboard', 'summary', { range }] as const)),
    attention: () => ['dashboard', 'attention'] as const,
    activity: () => ['dashboard', 'activity'] as const,
  },

  /* The staff roster every colleague-picker reads (GET /api/users).
   *
   * Top-level rather than beside the account list, because it is not that
   * resource: settings.users() keys the admin's /admin/users, which is a
   * different endpoint with a different audience and a much wider payload.
   * Two caches for two shapes — sharing one key would let an admin screen's
   * fetch populate a picker with data the picker's own callers may not read. */
  colleagues: () => ['colleagues'] as const,

  /* Who may be named the Operations owner of a converted case (S-1, GET
   * /api/users/operations-owners, guarded by quote.send). Top-level beside
   * `colleagues` and not under it: a narrower list from a different endpoint,
   * and nesting it would let a roster invalidation refetch it for no reason.
   * useConvertQuote re-reads it when the server refuses the owner the picker
   * offered. */
  operationsOwners: () => ['operationsOwners'] as const,

  /* Who may be asked for a quote's price (S-2a, GET /api/users/pricing-desk,
   * guarded by quote.team.assign). Top-level for operationsOwners' reason;
   * useRequestPricing re-reads it when the server refuses the assignee the
   * picker offered (`not-assignable`, `not-pricing`). */
  pricingDesk: () => ['pricingDesk'] as const,

  /* Every role's display name (GET /api/roles, story B3), for the places a
   * colleague's role is printed. Top-level for colleagues' reason: the admin
   * role list (`['settings', 'roles']`, /admin/roles) is a different endpoint
   * with a much wider payload and a narrower audience. The three role
   * mutations in features/settings/api/useRoles invalidate this key too. */
  roleNames: () => ['roleNames'] as const,

  /* «Мои задачи» (S-2b.1, GET /api/my-tasks). Its own top-level key: the
   * view spans quotes AND cases, so hanging it under either would let the
   * other's mutations miss it. It is re-read on every mount and on focus
   * rather than kept in step by mutations (see useMyTasks); the notification
   * stream also marks it stale on the topics that move a section
   * (`queriesMovedByTopic`). */
  tasks: {
    all: ['tasks'] as const,
    mine: () => ['tasks', 'mine'] as const,
  },

  quotes: {
    all: ['quotes'] as const,
    list: (filters?: Record<string, unknown>) => ['quotes', 'list', filters] as const,
    detail: (id: string) => ['quotes', 'detail', id] as const,
    /* The win/loss figures (Story 2.7, FR-15), KEYED ON THE RANGE — unlike
       dashboard.summary above, whose range is deliberately out of its key while
       the server ignores it. Here the server counts exactly the period it is
       given, so two periods are two different answers: sharing one key would
       serve last quarter's conversion rate under this month's label for as long
       as the entry stayed fresh, which is the one failure a figure with a
       period on it must not have. */
    statistics: (range: { from?: string; to?: string }) =>
      ['quotes', 'statistics', range] as const,
    /* The prefix every statistics key starts with. A quote changing status
       moves the figures for EVERY period at once, and the widget holds one
       entry per period the reader has looked at — so a mutation invalidates the
       prefix rather than the range it happens to be able to name. It cannot
       name one anyway: the period lives in the widget's own state, and a
       mutation fired from the board has never seen it. */
    statisticsAll: ['quotes', 'statistics'] as const,
    revisions: (id: string) => ['quotes', 'detail', id, 'revisions'] as const,
    /* Keyed under the quote's detail so a mutation that invalidates the quote
       refreshes its history with it — the entry the mutation just wrote is
       exactly what the open History tab should now show. */
    history: (id: string) => ['quotes', 'detail', id, 'history'] as const,
    /* The offer preview (UX-DR-15). Keyed under the quote's detail so editing
       the quote invalidates the preview with everything else — a preview that
       survived a price change would be showing the client an old number. The
       language is part of the key because it is part of the document. */
    offerPreview: (id: string, language?: string) =>
      ['quotes', 'detail', id, 'offer', language ?? 'default'] as const,
    /* The not-included items and disclaimers (Story 5.2, FR-60). Their own
       query rather than a field on the detail, because they have their own
       endpoint — and keyed UNDER the detail so sending the quote, which freezes
       them, refreshes the list with everything else it invalidates. */
    blocks: (id: string) => ['quotes', 'detail', id, 'blocks'] as const,
    /* The internal team (0028). Keyed under the quote's detail like the others,
       so invalidating the quote refreshes who is on it — and because staffing
       is what the row scope reads, a stale team is a stale answer to "who can
       see this". */
    team: (id: string) => ['quotes', 'detail', id, 'team'] as const,
    /* The contacts at the quote's own client, for the header's contact picker,
       read through the QUOTE's scope (GET /quotes/:id/client-contacts). Under
       the detail so a header save — which can change the client and therefore
       whose contacts these are — refreshes them with the record. */
    clientContacts: (id: string) =>
      ['quotes', 'detail', id, 'client-contacts'] as const,
  },

  cases: {
    all: ['cases'] as const,
    list: (filters?: Record<string, unknown>) => ['cases', 'list', filters] as const,
    detail: (id: string) => ['cases', 'detail', id] as const,
    legs: (id: string) => ['cases', 'detail', id, 'legs'] as const,
    /* ONE leg, on its own page (issue #35 story 3). Hung UNDER the case's
       detail key, and under the `legs` prefix inside it, so that the whole
       chain of invalidations already in this application reaches it: a leg
       write invalidates `cases.detail(caseId)`, and prefix matching carries
       that down to whichever leg page is open. A sibling key at the top level
       would have to be named by every mutation that can move a leg — including
       the ones fired from the case screen, which have never seen this page. */
    leg: (caseId: string, legId: string) =>
      ['cases', 'detail', caseId, 'legs', legId] as const,
    team: (id: string) => ['cases', 'detail', id, 'team'] as const,
    money: (id: string) => ['cases', 'detail', id, 'money'] as const,
    files: (id: string) => ['cases', 'detail', id, 'files'] as const,
    chat: (id: string) => ['cases', 'detail', id, 'chat'] as const,
    history: (id: string) => ['cases', 'detail', id, 'history'] as const,
    /* The case's document ARCHIVE list (`GET /api/documents?sourceKind=case`),
       the read-only «Бумаги работы» view's one read. Under the detail key so
       any write that invalidates `cases.detail(id)` refreshes it with no
       plumbing of its own. The status send no longer files anything here
       (S-15: its record is the send journal below); status PDFs filed before
       that still list. */
    archive: (id: string) => ['cases', 'detail', id, 'archive'] as const,
    /* The status send journal (S-15, `GET /api/cases/:id/status-sends`) and one
       entry with its stored email. Under the detail key so a send — which
       invalidates `cases.detail(id)` — lists its own row the moment it lands. */
    statusSends: (id: string) => ['cases', 'detail', id, 'status-sends'] as const,
    statusSend: (id: string, sendId: string) =>
      ['cases', 'detail', id, 'status-sends', sendId] as const,
    /* The source quote's conversation, read-only on the case (S-1, O3: GET
       /api/cases/:id/quote-conversation). Under `cases` rather than `chat`,
       because the CASE is what gates it and what the id names; nothing posts
       to it, so no chat mutation has a reason to reach it. */
    quoteConversation: (id: string) =>
      ['cases', 'detail', id, 'quote-conversation'] as const,

    /* Who a consolidated status send would reach, keyed by CLIENT rather than
       by case (FR-65). One address serves the whole send, so caching it per
       case would be the same answer stored N times and invalidated N ways —
       and it is not under `detail`, because no case is chosen yet when the
       sheet asks. */
    statusRecipient: (clientId: string) =>
      ['cases', 'status-recipient', clientId] as const,

    /* The status email previews (S-15): the server's render of what a send
       with these inputs would mail. The INPUTS are the key — language and the
       (debounced) note, and for the consolidated one the client and the sorted
       case ids — because the preview is an answer to a question rather than a
       resource of the case. Outside `detail` for that reason, and so a send's
       invalidation of the case does not re-render the email of a sheet that
       has just closed; the hooks set `staleTime: 0`, so an open sheet re-asks
       on every mount instead. */
    statusPreview: (id: string, language: string, note: string) =>
      ['cases', 'status-preview', id, { language, note }] as const,
    clientStatusPreview: (
      clientId: string,
      caseIds: readonly string[],
      language: string,
      note: string,
    ) => ['cases', 'client-status-preview', clientId, { caseIds, language, note }] as const,
  },

  /* The chat page (0029): conversations across records, and the thread beside
     them.

     Its own namespace rather than more keys under `cases`, because a thread
     may hang from a quote — and a quote's conversation cached under
     `['cases', …]` would be invalidated by a case mutation that has nothing to
     do with it.

     `thread` is keyed by SUBJECT rather than by conversation id, because that
     is what the caller holds: the page routes by record, and the conversation
     id only exists after the first read. */
  chat: {
    all: ['chat'] as const,
    conversations: () => ['chat', 'conversations'] as const,
    thread: (kind: string, subjectId: string) =>
      ['chat', 'thread', kind, subjectId] as const,
  },

  finance: {
    all: ['finance'] as const,
    aging: (range?: string) => ['finance', 'aging', { range }] as const,
    balances: (range?: string) => ['finance', 'balances', { range }] as const,
    /* The company-wide register of issued paper (N-30), keyed by period. Under
       `finance` so a payment, an allocation or an issue — each of which
       invalidates `finance.all` — refreshes what is outstanding on a row. */
    issuedDocuments: (range?: string) => ['finance', 'issued-documents', { range }] as const,
    /* One key for the cross-case log (`{ range }`), one case's payments
       (`{ caseId }`) and the all-time confirmation queue (`{ status: 'pending' }`,
       QW-10 — the sidebar's Finance badge, the payments tab's chip and the queue
       itself), because they are the same rows asked three questions — recording,
       allocating, confirming, rejecting or amending invalidates `finance.all` and
       therefore refreshes whichever of them is on screen. Allocations are NOT
       keyed separately: an allocation only ever exists inside a payment, and a
       second cache of them would be a second answer to "how much of this is
       still unallocated". */
    payments: (filters?: Record<string, unknown>) =>
      ['finance', 'payments', filters] as const,
    approvals: () => ['finance', 'approvals'] as const,
    /* One case's receivables by document (S-21): its issued invoices, its
       client's issued debit notes and its tranches, each with what is paid and
       what is left. Under `finance` so every payment write — record, allocate,
       reverse, amend, delete — refreshes it through `finance.all`, and so does
       an issue (which moves a tranche's money onto its invoice). Keyed by the
       payment currency and date the client payment sheet asks it in, which
       change the served `outstandingInPayment`; the case id comes first so a
       prefix reaches every variant of one case. */
    receivables: (caseId: string, payIn?: string, on?: string) =>
      ['finance', 'receivables', caseId, { payIn, on }] as const,
    /* A counterparty's statement and the counterparties list's figures
       (S-22e). Under `finance` although the pages are the counterparties
       section's: every figure on them moves with a payment, a cost line, a
       leg's or a unit's carrier, or a counterparty merge — and each of those
       already invalidates `finance.all`. Keyed per year: the server answers
       per year. */
    counterpartyStatement: (id: string, year: YearFilter) =>
      ['finance', 'counterparty-statement', id, year] as const,
    counterpartiesMoney: () => ['finance', 'counterparties-money'] as const,
    /* `documents` is GONE (issue #27). It keyed the showcase archive at
       `/api/finance/documents`; the real paper is read per case from
       `/cases/:id/invoices` and `/cases/:id/debit-notes`, whose keys hang off
       `cases.detail(id)` so that one case invalidation reaches both lists and
       the money read they move. A key left here would be an invitation to
       cache the same rows a second time under a prefix nothing invalidates. */
  },

  /* The company's own legal entities, as every non-admin surface reads them —
   * the list a payment names when it says which of our entities the money
   * reached (Story 4.5).
   *
   * Top-level rather than under `finance`, for the reason `colleagues` is:
   * it is not a finance resource, it is a reference list finance happens to
   * read, and keying it under `finance` would make every recorded payment
   * invalidate a list that a payment cannot change. */
  issuingEntities: () => ['issuing-entities'] as const,

  directories: {
    all: ['directories'] as const,
    clients: (filters?: Record<string, unknown>) =>
      ['directories', 'clients', filters] as const,
    /** The detail read, which is the only one that carries contacts. */
    client: (id: string) => ['directories', 'clients', 'detail', id] as const,
    /** The composed overview, keyed by the year it was asked for — the server
     *  answers per year, so the year belongs in the key (the rule
     *  quotes.statistics states). Under the `directories` prefix so a client
     *  write's invalidation reaches it. */
    clientsOverview: (year: YearFilter, search?: string) =>
      ['directories', 'clients', 'overview', year, search ?? ''] as const,
    /** One client's yearly statement — under the detail key for the same
     *  reason, and so opening the client warms nothing it should not. */
    clientStatement: (id: string, year: YearFilter) =>
      ['directories', 'clients', 'detail', id, 'statement', year] as const,
    transport: (filters?: Record<string, unknown>) =>
      ['directories', 'transport', filters] as const,
  },

  /* Counterparties (S-22b): every company or person that provides us a service,
     their own section since the directory's «Участники» tab left. A namespace
     of their own rather than under `directories`, so a client write does not
     refetch every carrier picker on screen and a counterparty write reaches
     exactly the readers below.

     `list` is keyed by its filters — the section asks for everything once
     (`includeInactive`), a picker for its categories and search — because the
     server answers a different list to each. `detail` is the single read, the
     only one carrying contacts: an edit form seeds from it and never from a
     list row. */
  counterparties: {
    all: ['counterparties'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['counterparties', 'list', filters] as const,
    detail: (id: string) => ['counterparties', 'detail', id] as const,
  },

  /* Company settings.
   *
   * Only the direction templates and the account list are keyed here. The
   * older settings hooks each
   * carry their own local key constant (`['settings', 'templates']` and
   * friends), which features/README.md records as drift against this very
   * factory — a new slice therefore lands HERE, and moving the four existing
   * ones is a change of its own rather than a rider on this story. */
  settings: {
    /* The prefix every directions key starts with, and the one a mutation
       invalidates. It cannot name the narrowing the reader happens to be
       looking at: the settings screen holds the unfiltered list and a case
       leg's picker holds the active-only one, and a create fired from either
       moves both. */
    directionsAll: ['settings', 'directions'] as const,
    /* The named routes a case leg is filled from (issue #35 story 1).
       `activeOnly` is part of the key because the server answers a DIFFERENT
       list for it: Settings maintains every direction including the switched-
       off ones, while a picker may only offer the live ones. One shared key
       would serve the picker exactly the retired routes it exists to hide. */
    directions: (activeOnly?: boolean) =>
      (activeOnly
        ? (['settings', 'directions', { active: true }] as const)
        : (['settings', 'directions'] as const)),
    /* The account list (GET /admin/users, admin.users.manage) — the Settings
       «Пользователи» tab since S-7. Under `settings` because that is where it
       is managed, and because useAmendRole invalidates it: amending a role
       changes every holder's effective capabilities. */
    users: () => ['settings', 'users'] as const,
    /* The wording a new quote is seeded with (GET /settings/templates/
       standing-defaults, N-19). Read-only and built into the product, so no
       mutation invalidates it; it is NOT under `['settings', 'templates']`,
       which every template write refetches. */
    standingDefaults: () => ['settings', 'standing-defaults'] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    feed: () => ['notifications', 'feed'] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
  },
} as const
